/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { cn } from '@/lib/utils';

interface SegmentPaginationProps {
    count: number;
    activeIndex: number;
    className?: string;
}

/**
 * Equal-width segment bar — active segment is solid, inactive segments are muted.
 * Used on PLP tile carousels (hover reveal) and PDP mobile image gallery.
 */
export function SegmentPagination({ count, activeIndex, className }: SegmentPaginationProps) {
    return (
        <div aria-hidden="true" className={cn('flex h-0.5', className)} data-testid="segment-pagination">
            {Array.from({ length: count }, (_, i) => (
                <div
                    key={i}
                    className={cn(
                        'flex-1 h-full transition-colors duration-150',
                        i === activeIndex ? 'bg-foreground' : 'bg-foreground/30'
                    )}
                />
            ))}
        </div>
    );
}
